import { useState } from 'react';
import { MatchRing, Tag } from './MatchRing';
import { LuEye } from 'react-icons/lu';
import { RiMailSendLine, RiSettingsLine } from 'react-icons/ri';
import { HiCheck } from 'react-icons/hi';
import { FaRegKeyboard } from 'react-icons/fa';
import CandidateAction from './CandidateAction';
import CandidateDetails from './CandidateDetails';

const avatarColors = [
  '#c7d2fe',
  '#bfdbfe',
  '#fde68a',
  '#bbf7d0',
  '#fecaca',
  '#e9d5ff',
];

const getAvatarColor = (id: string) => {
  const hash = parseInt(id.slice(-4), 16);
  return avatarColors[hash % avatarColors.length];
};

const statusStyle = {
  New: { bg: 'bg-[#EAEAEA]', color: 'text-[#606060]' },
  Shortlisted: { bg: 'bg-[#DAFFE3]', color: 'text-[#606060]' },
  Rejected: { bg: 'bg-red-100', color: 'text-[#606060]' },
  'Call for Interview': { bg: 'bg-[#FFF4CC]', color: 'text-[#606060]' },
};

export const PersonRow = ({ person, onToggle, disabled }) => {
  const [hovered, setHovered] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);

  const ss = statusStyle[person.status] || statusStyle.New;
  const showCheckbox = hovered || person.checked;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`flex items-center gap-3 px-2 py-1.5 rounded-lg relative transition-all cursor-default overflow-x-auto max-w
        ${
          person.checked
            ? 'bg-purple-50 border-purple-300 border-[1.5px]'
            : 'bg-white  border-[#D4D4D4] border-[0.5px]'
        }
        ${hovered ? 'shadow-lg' : 'shadow-sm'}`}
    >
      <div className="flex items-center flex-shrink-0 relative">
        {!disabled && (
          <div
            onClick={() => onToggle(person._id)}
            className={`w-4 h-4 rounded-md flex items-center justify-center cursor-pointer transition-opacity
              ${showCheckbox ? 'opacity-100' : 'opacity-0'}
              ${person.checked ? 'bg-[#6E3EF5]' : 'bg-[#E9E7E7]'}`}
          >
            {person.checked && <HiCheck className="text-white w-2.5 h-2.5" />}
          </div>
        )}
        <div
          className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 text-sm font-bold text-gray-500 overflow-hidden ml-2 transition-all"
          style={{ backgroundColor: getAvatarColor(person._id) }}
        >
          {person.name
            .split(' ')
            .map((n) => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()}
        </div>
      </div>

      {/* Name & Info */}
      <div className="max-w-[160px]">
        <p className="m-0 text-xs font-inter font-semibold">{person.name}</p>
        <p className="mt-0.5 text-[10px] text-[#606060] flex items-center gap-1">
          <FaRegKeyboard color="#6E3EF5" />
          {person.title} • {person.location} • {person.experienceYears}
        </p>
      </div>

      {/* Match Ring & Jobs */}
      <MatchRing value={person.primaryJob?.match ?? 0} />
      <div className="max-w-[100px]">
        <p className="text-[8px] text-gray-900 mt-0.5">
          Pr.: {person.primaryJob?.name ?? 'N/A'} ({person.primaryJob?.match ?? 0}%)
        </p>
        {person.secondaryJobs?.length > 0 && (
          <p className="text-[7px] font-normal text-gray-400 m-0">
            Sec:{' '}
            {person.secondaryJobs
              .slice()
              .sort((a, b) => (b?.match ?? 0) - (a?.match ?? 0))
              .map((job) => `${job?.name ?? 'N/A'} (${job?.match ?? 0}%)`)
              .join(', ')}
          </p>
        )}
      </div>

      {/* Skills */}
      <div className="flex-1">
        <p className="text-[9px] text-gray-400 flex items-center gap-1 mb-1">
          <RiSettingsLine color="#6E3EF5" />
          Skills
        </p>
        <div className="flex flex-wrap gap-1">
          {person.skills?.slice(0, 5).map((s) => (
            <Tag key={s} label={s} />
          ))}
        </div>
      </div>

      {/* Status */}
      <span
        className={`${ss.bg} ${ss.color} text-[10px] font-bold px-3 py-1 rounded-full min-w-[60px] text-center`}
      >
        {person.status}
      </span>

      {/* Actions */}
      <div className="flex gap-1">
        <button
          className="w-5 h-5 rounded-md bg-gradient-to-r from-[#6E3EF5] to-[#CDBCFF] flex items-center justify-center transition-colors hover:bg-purple-200"
          onClick={() => setDetailsOpen(true)}
        >
          <LuEye color="white" size={12} />
        </button>
        <button
          className="w-5 h-5 rounded-md bg-[#CDBCFF] flex items-center justify-center transition-colors hover:bg-purple-200"
          onClick={() => setActionOpen(true)}
        >
          <RiMailSendLine color="#272727" size={12} />
        </button>
      </div>

      {/* Modals */}
      <CandidateAction
        open={actionOpen}
        onClose={() => setActionOpen(false)}
        candidate={person}
      />
      <CandidateDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        candidate={person}
      />
    </div>
  );
};
